import { IModuleProps, IModules } from './interfaces/interfaces';

// ADD import $COMPONENT as a Module in /modules;
import ProductModule from './components/modules/ProductModule/ProductModule';
import LinkedIconModule from './components/modules/LinkedIconModule/LinkedIconModule';
import RegistrationModule from './components/modules/RegistrationModule/RegistrationModule';
import SearchModule from './components/modules/SearchModule/SearchModule';
import PersonalizationModule from './components/modules/PersonalizationModule/PersonalizationModule';
import AdvantageCommunicationModule from './components/modules/AdvantageCommunicationModule/AdvantageCommunicationModule';
import ProductModulePreview from './components/modules/ProductModule/ProductModule-Preview';
import LinkedIconPreview from './components/modules/LinkedIconModule/LinkedIcon-preview';
import InfocenterModule from './components/modules/InfocenterModule/InfocenterModule';
import FavouriteListModule from './components/modules/FavouriteListModule/FavouriteListModule';
import TextModule from './components/modules/TextModule/TextModule';
import ContactModule from './components/modules/ContactModule/ContactModule';
import AppStoreModule from './components/modules/AppStoreModule/AppStoreModule';
import FormsModule from './components/modules/FormsModule/FormsModule';

type ModuleKey = keyof {
  // ADD $MODULE_KEY: string;
  ProductModule: string;
  LinkedIconModule: string;
  RegistrationModule: string;
  SearchModule: string;
  PersonalizationModule: string;
  AdvantageCommunicationModule: string;
  InfocenterModule: string;
  FavouriteListModule: string;
  OnboardingModule: string;
  TextModule: string;
  ContactModule: string;
  AppStoreModule: string;
  FormsModule: string;
};

const moduleComponents: IModuleComponents = {
  // ADD $MODULE_KEY: $COMPONENT;
  ProductModule,
  LinkedIconModule,
  RegistrationModule,
  SearchModule,
  PersonalizationModule,
  AdvantageCommunicationModule,
  InfocenterModule,
  FavouriteListModule,
  TextModule,
  ContactModule,
  AppStoreModule,
  FormsModule,
};

const modulePreviewComponents: IModuleComponents = {
  // ADD $MODULE_KEY: $COMPONENT;
  ProductModule: ProductModulePreview,
  LinkedIconModule: LinkedIconPreview,
  SearchModule: LinkedIconPreview,
  PersonalizationModule: LinkedIconPreview,
  InfocenterModule: LinkedIconPreview,
  FavouriteListModule: LinkedIconPreview,
  ContactModule: LinkedIconPreview,
  TextModule: LinkedIconPreview,
  AppStoreModule: LinkedIconPreview,
  FormsModule: LinkedIconPreview,
};

interface IModuleComponents {
  [key: string]: React.FC<IModuleProps>;
}

export const getModuleComponents = (modules: IModules): IModules => {
  const modulesWithComponents: IModules = {};
  Object.keys(modules).forEach((moduleName) => {
    if (moduleComponents[moduleName]) {
      const moduleComponent = modules[moduleName as ModuleKey];
      modulesWithComponents[moduleName] = {
        ...moduleComponent,
        moduleName,
        component: moduleComponents[moduleName],
        previewComponent: modulePreviewComponents[moduleName],
      };
    } else {
      // eslint-disable-next-line no-console
      console.error('module', moduleName, "doe's not exists as a component");
    }
  });

  return modulesWithComponents;
};

export const availableModules = {
  ProductModule: {
    description: '',
    defaultProps: {
      headline: '',
      creditId: '',
      textContent: '',
      linkText: '',
      linkHref: '',
      containerClassName: 'product',
    },
  },

  LinkedIconModule: {
    description: '',
    defaultLayout: {
      w: 1,
      h: 1,
      minW: 1,
      maxW: 1,
      minH: 1,
      maxH: 1,
      static: true,
    },
    defaultProps: {
      icon: '',
      linkText: '',
      linkHref: '',
      linkTarget: '',
    },
  },

  AdvantageCommunicationModule: {
    description: '',
    defaultLayout: {
      w: 2,
      h: 1,
      minW: 2,
      maxW: 2,
      minH: 1,
      maxH: 1,
      static: true,
    },
    defaultProps: {
      headline: '',
      personalisationLink: '',
      personalisationText: '',
      advantageList: '',
      containerClassName: 'lightblue',
    },
  },

  RegistrationModule: {
    description: '',
    defaultLayout: {
      w: 3,
      h: 1,
      minW: 3,
      maxW: 3,
      minH: 1,
      maxH: 1,
      static: true,
    },
    defaultProps: {
      headline: '',
      textContent: '',
      containerClassName: 'lightblue',
    },
  },

  SearchModule: {
    description: '',
    defaultLayout: {
      w: 3,
      h: 1,
      minW: 3,
      maxW: 3,
      minH: 1,
      maxH: 1,
      static: true,
    },
    defaultProps: {
      headline: '',
      textContent: '',
      containerClassName: 'lightblue',
      searchActionUrl: '',
    },
  },

  PersonalizationModule: {
    description: '',
    defaultProps: {
      headline: '',
      linkText: '',
    },
  },

  AppStoreModule: {
    description: '',
    defaultProps: {
      headline: '',
      linkText: '',
    },
  },

  InfocenterModule: {
    description: '',
    defaultProps: {
      headline: '',
      textContent: '',
      phone: '',
      phoneHref: '',
      phoneAriaLabel: '',
      noticeCost: '',
      serviceTime: '',
      linkText: '',
      linkHref: '',
      linkTarget: '',
    },
  },

  FavouriteListModule: {
    description: '',
    defaultLayout: {
      w: 2,
      h: 1,
      minW: 2,
      maxW: 2,
      minH: 1,
      maxH: 1,
      static: true,
    },
    defaultProps: {
      favorites: [],
    },
    /* resizeOptions: [ // Resize deactivated see: https://jira.adesso.de/browse/KFWLINIE-2977
      { w: 2, h: 1, title: 'cockpit.minimize' },
      { w: 2, h: 2, title: 'cockpit.maximize' },
    ], */
  },

  TextModule: {
    description: '',
    defaultProps: {
      headlineContentLevel: '3',
      headlineContent: '',
      textContent: '',
      linkText: '',
      linkHref: '',
      linkTarget: '_blank',
    },
  },

  ContactModule: {
    description: '',
    defaultProps: {
      headline: '',
    },
    isEditable: true,
    persistProps: ['contactEmail', 'contactName', 'contactDescription', 'contactPhone', 'contactPhoneMobile'],
  },

  FormsModule: {
    description: '',
    defaultLayout: {
      w: 2,
      h: 1,
      minW: 1,
      maxW: 2,
      minH: 1,
      maxH: 2,
      static: false,
    },
    defaultProps: {},
    /* resizeOptions: [ // Resize deactivated see: https://jira.adesso.de/browse/KFWLINIE-2977
      { w: 2, h: 1, title: 'Verkleinern' },
      { w: 2, h: 2, title: 'Vergrößern' },
    ], */
    fixLayouts: {
      xs: {
        w: 1,
        h: 1,
      },
    },
  },
};
